import React, { useEffect, useState } from "react";
import {Link} from "react-router-dom";
import { IonPage, IonContent, IonFooter, createGesture } from "@ionic/react";
import Nav from "./Nav";
import { sidnavStore, SidenavState } from "../api/SidenavStore";
import { ApiService } from "../api/ApiService";
import { getRecentClaimGroup } from "../utils/ClaimUtils";
import { UserState, userStore } from "../api/AuthService";
import { UserUtils } from "../utils/UserUtils";
import HeroChrome from "../components/HeroChrome";
import { AgreementsIcon, CameraIcon, DocIcon, SafetyIcon } from "../components/icons";
import { AccidentSvg, BenefitsSvg, BreakdownSvg } from "../components/svgIcons";
import { config } from "../Constants";
import { runStatus } from "../components/walkthrough/FirstSignIn";
import { useLogPageVisit } from "../utils/logPageVisit";

//walkthrough import
import DocsFirstIn from "../components/walkthrough/DocsFirstIn";

function useDocsData() {
    useLogPageVisit('LP Docs', 'User viewed the Docs page');
    const { driver } = userStore((state: UserState) => ({ driver: state.driver }));

    const [mounted, setMounted] = useState(false);
    const [msgCount, setMsgCount] = useState(0);
    const [notificationCount, setNotificationCount] = useState(0);
    const [hasOpenClaim, setHasOpenClaim] = useState(false);

    useEffect(() => {
        const id = requestAnimationFrame(() => {
            setTimeout(() => setMounted(true), 30);
        });
        return () => cancelAnimationFrame(id);
    }, []);

    useEffect(() => {
        if (!driver?.unit) {
            return;
        }

        ApiService.claimCheck('docs', driver.unit)
            .then(claims => setHasOpenClaim(!!getRecentClaimGroup(claims).mostRecentClaim))
            .catch(() => setHasOpenClaim(false));
    }, [driver?.unit]);

    useEffect(() => {
        ApiService.chatConversationGet('docs', {})
            .then((it: any) => {
                if (!Array.isArray(it)) return;
                let count = 0;
                for (let i = 0; i < it.length; i++) {
                    if (it[i]?.lastMessage?.read !== 1) count += 1;
                }
                setMsgCount(count);
            })
            .catch(() => {});

        if (!UserUtils.isAdmin() && !UserUtils.isDriverManager() && !UserUtils.isInspector()) {
            ApiService.getPushNotifications('docs')
                .then((it: any) => {
                    if (!Array.isArray(it)) return;
                    setNotificationCount(it.filter((notification: any) => !notification.read).length);
                })
                .catch(() => {});
        }
    }, []);

    return { mounted, msgCount, notificationCount, hasOpenClaim };
}

const Docs = () => {
    const {close}: { close: () => void } = sidnavStore((state: SidenavState) => ({open: state.open, close: state.close}));
    const {toggleSidenav}: { toggleSidenav: () => void } = sidnavStore((state: SidenavState) => ({toggleSidenav: state.toggle}));

    const ref = React.useRef<HTMLDivElement>(null);
    const docsData = useDocsData();

    useEffect(() => {
        if (config.showSidenav) gestureInit();
    }, []);

    const gestureInit = () => {
        const card = ref.current;
        if (!card) return;
        const gesture = createGesture({
            el: card,
            gestureName: 'swipeMenu',
            onMove: () => {},
            onEnd: () => {
                runStatus ? console.log('locked') : toggleSidenav();
            },
        });
        gesture.enable();
    }

    return (
        <IonPage>
            <IonContent fullscreen className="design-a-content">
                <DocsFirstIn>
                    <div
                        ref={ref}
                        className={`design-a${docsData.mounted ? ' design-a--mounted' : ''}`}
                        onClick={() => close()}
                    >
                        <div className="home-hero">
                            <HeroChrome
                                onMenuClick={() => toggleSidenav()}
                                msgCount={docsData.msgCount}
                                notificationCount={docsData.notificationCount}
                            />

                            <div className="docs-hero">
                                <div className="docs-hero__eyebrow">Docs &amp; Resources</div>
                                <div className="docs-hero__title home-display">Find what you need</div>
                            </div>
                        </div>

                        <div className="home-sheet">
                            <div id="WholeDocsSection">
                                <div className="docs-tile-grid">
                                    <Link to="/safety" className="docs-tile">
                                        <div className="docs-tile__icon">
                                            <SafetyIcon />
                                        </div>
                                        <div className="docs-tile__title">Safety</div>
                                    </Link>

                                    <Link to="/maintenancevids" className="docs-tile">
                                        <div className="docs-tile__icon">
                                            <BreakdownSvg />
                                        </div>
                                        <div className="docs-tile__title">Maintenance</div>
                                    </Link>

                                    <Link to="/benefits" className="docs-tile">
                                        <div className="docs-tile__icon">
                                            <BenefitsSvg />
                                        </div>
                                        <div className="docs-tile__title">Benefits</div>
                                    </Link>

                                    <Link to="/permits" className="docs-tile">
                                        <div className="docs-tile__icon">
                                            <DocIcon />
                                        </div>
                                        <div className="docs-tile__title">Permits</div>
                                    </Link>
                                </div>

                                <div className="docs-rows">
                                    <Link to="/contracts" className="docs-row" id="Contracts">
                                        <div className="docs-row__icon">
                                            <AgreementsIcon />
                                        </div>
                                        <div className="docs-row__label">Contracts</div>
                                    </Link>
                                    {docsData.hasOpenClaim ? (
                                        <Link to="/claims" className="docs-row" id="Claim">
                                            <div className="docs-row__icon">
                                                <CameraIcon />
                                            </div>
                                            <div className="docs-row__label">Claim Photos</div>
                                        </Link>
                                    ) : null}
                                    <Link to="/accidentinfo" className="docs-row docs-row--accident" id="Accidents">
                                        <div className="docs-row__icon">
                                            <AccidentSvg color="#b42318" />
                                        </div>
                                        <div className="docs-row__label">Accidents</div>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </DocsFirstIn>
            </IonContent>
            <IonFooter>
                <Nav pageName="Docs" onSearch={function (target: boolean): void {
                    console.log("Not ready");
                } }/>
            </IonFooter>
        </IonPage>
    )
}

export default Docs
